import { useState } from "react";
// The classic useSignIn (with authenticateWithRedirect) lives under the
// legacy subpath in @clerk/react v6+; the root export is the new signals API.
import { useSignIn } from "@clerk/react/legacy";
import { useT } from "@/i18n";
import { CLERK_PENDING_KEY, clerkEnabled } from "@/lib/clerk";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

/**
 * "Continue with Google" button. Starts Clerk's OAuth redirect flow; on
 * return, <GoogleAuthBridge> exchanges the Clerk session for a kd_session.
 * Renders nothing when Clerk isn't configured, so the email/password form
 * is unaffected in environments without a publishable key.
 */
export function GoogleSignInButton() {
  if (!clerkEnabled) return null;
  return <GoogleSignInButtonInner />;
}

function GoogleSignInButtonInner() {
  const { signIn, isLoaded } = useSignIn();
  const t = useT();
  const [busy, setBusy] = useState(false);

  async function onClick() {
    if (!signIn || busy) return;
    setBusy(true);
    // Mark the exchange as user-initiated BEFORE leaving the page — the
    // bridge refuses to exchange stale Clerk sessions without this flag.
    localStorage.setItem(CLERK_PENDING_KEY, "1");
    try {
      await signIn.authenticateWithRedirect({
        strategy: "oauth_google",
        redirectUrl: `${basePath}/sso-callback`,
        // Return to where the user started so modal/gate flows resume.
        redirectUrlComplete: window.location.pathname + window.location.search,
      });
    } catch {
      localStorage.removeItem(CLERK_PENDING_KEY);
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={!isLoaded || busy}
        className="w-full flex items-center justify-center gap-2 border border-foreground bg-background py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-foreground hover:text-background transition-colors disabled:opacity-60"
      >
        <GoogleMark />
        {busy ? t("auth.working") : t("auth.continueWithGoogle")}
      </button>
      <div className="flex items-center gap-3 my-4">
        <span className="h-px flex-1 bg-foreground/30" />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          {t("auth.orDivider")}
        </span>
        <span className="h-px flex-1 bg-foreground/30" />
      </div>
    </>
  );
}

function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.27 14.29a7.19 7.19 0 0 1 0-4.58V6.62H1.29a12.04 12.04 0 0 0 0 10.76l3.98-3.09z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.69 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"
      />
    </svg>
  );
}
